import "./SiteStatus/sitestatus.scss";
import {
    type FC,
    type ReactNode,
    useCallback,
    useEffect,
    useState,
} from "react";
import { Icon, Tooltip } from "@onshopfront/components";
import { UPLOAD_ERROR_EVENT } from "#/_Bootstrap/GlobalConstants.ts";
import { useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import { LAYOUT_SYNCHRONISE_TOGGLE } from "#/Actions/General/LayoutActions.ts";
import type { QueuedItem } from "#/Data/_Bases/BaseUploadRepository.ts";
import { determineDataSource } from "#/Data/DataStores/DataSource.ts";
import { fireEventType } from "#/Utilities/Generic/ActionsHelper.ts";
import { DISABLE_EMBEDDED } from "#/Utilities/Platform/CurrentRequest.ts";
import { UPLOAD_RETRY_LIMIT, type UPLOAD_TYPES } from "#/Workers/Upload/UploadEvents.ts";
import { SynchroniseMenu } from "./SiteStatus/SynchroniseMenu";

export const SiteStatus: FC = () => {
    const dispatch = useShopfrontDispatch();

    const syncStatus = useShopfrontSelector(state => state.synchronisation.status);
    const online = useShopfrontSelector(state => state.information.online);
    const uploading = useShopfrontSelector(state => state.synchronisation.uploading);
    const menu = useShopfrontSelector(state => state.layout.synchronise.open);

    const [ errors, setErrors ] = useState<Array<QueuedItem<UPLOAD_TYPES>>>([]);

    const loadErrors = useCallback(async (event?: CustomEventInit<{ outstanding?: boolean; }>) => {
        let attemptCount = UPLOAD_RETRY_LIMIT;

        if(event?.detail?.outstanding) {
            attemptCount = 1;
        }

        const source = await determineDataSource();
        const uploads = await source.uploads.getFailedUploads(attemptCount);

        setErrors(uploads);
    }, []);

    // Load the upload errors after the upload queue has run
    useEffect(() => {
        if(uploading) {
            return;
        }

        void loadErrors();
    }, [ uploading, loadErrors ]);

    // Add upload error listener to the document
    useEffect(() => {
        if(typeof document === "undefined") {
            return;
        }

        document.addEventListener(UPLOAD_ERROR_EVENT, loadErrors);

        return () => {
            document.removeEventListener(UPLOAD_ERROR_EVENT, loadErrors);
        };
    }, [ loadErrors ]);

    const classes = [ "header-item" ];

    let status = "OFFLINE";

    if(online) {
        classes.push("online");
        status = "ONLINE";
    }

    if(syncStatus === "running") {
        classes.push("syncing");
    }

    let icon: ReactNode;

    if(uploading) {
        icon = (
            <span className="uploading">
                <Icon icon={[ "fal", "arrow-alt-up" ]} />
            </span>
        );
    } else if(errors.length && online) {
        classes.push("errors");
        icon = (
            <span className="error">
                <Icon icon={[ "fal", "exclamation-triangle" ]} />
            </span>
        );
    } else if(DISABLE_EMBEDDED) {
        classes.push("warning");
        icon = (
            <span className="icon">
                <Icon icon={[ "fal", "exclamation-triangle" ]} />
            </span>
        );
    } else {
        icon = (
            <span className="globe">
                <Icon icon={[ "fal", "globe" ]} />
            </span>
        );
    }

    if(menu) {
        classes.push("menu-open");
    }

    return (
        <div
            id="site-status"
            className={classes.join(" ")}
            data-testid="sh-status"
        >
            <Tooltip
                title="We're currently synchronising your data"
                disabled={syncStatus !== "running"}
                location="bottom"
            >
                {(ref, getProps) => (
                    <div
                        ref={ref}
                        className="status"
                        onClick={e => {
                            e.preventDefault();

                            if(!menu && (syncStatus === "running" || uploading)) {
                                return;
                            }

                            dispatch(fireEventType(LAYOUT_SYNCHRONISE_TOGGLE));
                        }}
                        {...getProps()}
                    >
                        <div className="status-icon">
                            {icon}
                        </div>
                        <p>{status}</p>
                    </div>
                )}
            </Tooltip>
            <SynchroniseMenu
                embeddedDisabled={DISABLE_EMBEDDED}
                errors={errors}
                onRefresh={loadErrors}
            />
        </div>
    );
};
